/* eslint-disable max-len */
import axios, { AxiosRequestConfig } from "axios";
import { JobSearchRequest } from "@themuse/design-system";
import getConfig from "next/config";
import NodeCache from "node-cache";

import { formatLog } from "utils/logging";
import { getMuseJobsConfig } from "orm/jobs";
import { getLocationConfig } from "orm/locations";

import { JobTitleCities } from "interfaces/api/JobTitleList";
import { CompanySuggestorResults } from "interfaces/api/suggestor";
import { JobAlertsSubmissionValues, NewsletterSubmissionValues } from "interfaces/api/jobAlerts";

import { mockCompanies } from "mocks/mockCompanies";
import mockCompanyAttributes from "mocks/mockCompanyAttributes.json";
import mockRecommendations from "mocks/mockRecommendations.json";
import mockJobs from "mocks/jobs.json";
import mockLocationJson from "mocks/locations.json";
import { mockLocations } from "mocks/mockLocations";
import mockJobsCities from "mocks/mockJobsCities.json";
import { getFetchError } from "./errors";

import { suggestionApiQueryMaxlength } from "./constants";

/**
 * Creates a new instance of the NodeCache class
 * - standard time-to-live (TTL) of 86400 seconds
 * - check period of 300 seconds.
 */
const apiCache = new NodeCache({ stdTTL: 86400, checkperiod: 300 });

export class ApiError extends Error {
    private statusCode: number;

    constructor(name: string, statusCode: number, message?: string) {
        super(message);
        this.name = name;
        this.statusCode = statusCode;
    }
}

/**
 * Request company suggestions.
 */
export const fetchCompanySuggestions = async (inputValue: string) => {
    const {
        publicRuntimeConfig: { apiBase, apiRequestTimeoutMs, appName },
        serverRuntimeConfig: { mockApiResponses }
    } = getConfig();
    const path = `${apiBase}/companies?query=${inputValue}`;
    try {
        const response = mockApiResponses
            ? { data: mockCompanies(inputValue) }
            : await axios(path, { timeout: apiRequestTimeoutMs });
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request company suggestions from ${path}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Returns the Axios config for requesting Jobs from TheMuse.
 */
export const getCompanyAttributesConfig = (): AxiosRequestConfig => {
    const { serverRuntimeConfig: { companyApiHost, companyApiToken } } = getConfig();
    const { publicRuntimeConfig: { apiRequestTimeoutMs } } = getConfig();
    const path = `${companyApiHost}/api/v1/companies/attributes`;

    const museConfig: AxiosRequestConfig = {
        method: ("GET"),
        url: path,
        timeout: apiRequestTimeoutMs,
        headers: {
            Authorization: `Bearer ${companyApiToken}`
        }
    };

    return museConfig;
};

export const fetchCompanyAttributes = async (): Promise<any> => {
    const { serverRuntimeConfig: { appName, mockApiResponses } } = getConfig();
    const axiosConfig: AxiosRequestConfig = getCompanyAttributesConfig();
    try {
        const response = mockApiResponses
            ? { data: mockCompanyAttributes }
            : await axios(axiosConfig);
        const ormResults = response.data;
        return ormResults;
    } catch (error) {
        const msg = `${appName} was unable to request company attributes from ${axiosConfig.url}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request location suggestions.
 */
export const fetchLocationSuggestions = async (inputValue: string) => {
    const { serverRuntimeConfig: { appName, mockApiResponses } } = getConfig();
    let config;
    if (!inputValue) {
        return null;
    }
    try {
        config = getLocationConfig(inputValue);
        const response = mockApiResponses
            ? { data: mockLocationJson }
            : await axios(config);
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request locations suggestions from ${config?.url}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request job recommendations.
 */
export const fetchJobRecommendations = async () => {
    const {
        publicRuntimeConfig: { apiBase, apiRequestTimeoutMs, appName },
        serverRuntimeConfig: { mockApiResponses }
    } = getConfig();
    const path = `${apiBase}/recommendations`;
    try {
        const response = mockApiResponses
            ? { data: mockRecommendations }
            : await axios.get(path, { timeout: apiRequestTimeoutMs });
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request job recommendations from ${path}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request jobs directly from the backend API or proxied through the Next.js internal API.
 */
export const fetchJobs = async (queryParams: JobSearchRequest, ctsEnabled: boolean, cookies?: string) => {
    const {
        publicRuntimeConfig: { appName },
        serverRuntimeConfig: { mockApiResponses }
    } = getConfig();
    const axiosConfig: AxiosRequestConfig = getMuseJobsConfig(queryParams, ctsEnabled, cookies);

    try {
        const response = mockApiResponses
            ? { data: mockJobs }
            : await axios(axiosConfig);
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request job results from ${axiosConfig.url}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request company suggestions from the backend API.
 */
export const fetchApiCompanySuggestions = async (query: string): Promise<CompanySuggestorResults> => {
    const {
        serverRuntimeConfig: {
            apiRequestTimeoutMs, jobsApiHost, appName, mockApiResponses
        }
    } = getConfig();
    // if query exists we will pass it in the params.
    const queryObj = query ? { query } : {};
    const config = { params: { type: "company_name", ...queryObj }, timeout: apiRequestTimeoutMs };
    const path = `${jobsApiHost}/api/v2/suggestions`;
    try {
        const response = mockApiResponses
            ? { data: mockCompanies(queryObj.query) }
            : await axios(path, config);
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request job suggestions server side from ${path}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request locations from the backend API.
 */
export const fetchLocations = async (userInput: string) => {
    const location = userInput?.substring(0, suggestionApiQueryMaxlength);
    const {
        serverRuntimeConfig: { jobsApiHost, mockApiResponses },
        publicRuntimeConfig: { apiRequestTimeoutMs, appName }
    } = getConfig();
    const config = {
        params: {
            type: "location",
            query: location ?? ""
        },
        timeout: apiRequestTimeoutMs
    };
    const path = `${jobsApiHost}/api/v2/suggestions`;
    try {
        const response = mockApiResponses
            ? { data: mockLocations(location) }
            : await axios.get(path, config);
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request locations server side from ${path}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

export const formatLocationResponse = (response: any) => response.map((location: any) => ({
    label: location.text,
    value: location.text,
    latitude: location.value.latitude,
    longitude: location.value.longitude,
    remote_work_location_type: location.value.remote_work_location_type
}));

export const fetchApiLocationSuggestions = async (userInput: string): Promise<any> => {
    const locationsResponse = await fetchLocations(userInput);
    return formatLocationResponse(locationsResponse.data);
};

export const formatCompanySuggestions = (suggestions: CompanySuggestorResults["data"]) => (
    suggestions.map((company) => ({
        label: company.text,
        value: company.value.short_name
    }))
);

/**
 * job alerts optin from the public API.
 */
export const jobAlertsSubscribeRequest = async (values: JobAlertsSubmissionValues, axiosOptions?: AxiosRequestConfig) => {
    const { publicRuntimeConfig: { apiRequestTimeoutMs, apiBase } } = getConfig();

    const config: AxiosRequestConfig = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                "Content-Type": "application/json",
            }
        },
        ...axiosOptions
    };
    const path = `${apiBase}/job_alerts/subscribe`;

    try {
        const response = await axios.post(path, values, config);
        if (response.status === 200 || response.status === 201) {
            return true;
        }
        throw new Error(`Job Alerts subscription was unsuccessful. Status: ${response.status}`);
    } catch (error) {
        const msg = "Error: Job Alerts subscription was unsuccessful.";
        const err = JSON.stringify(error.response.data);
        console.error(msg, err);
        return false;
    }
};

/**
 * job alerts optin from the museweb service API.
 */
export const jobAlertsApiSubscribeRequest = async (values: JobAlertsSubmissionValues, axiosOptions?: AxiosRequestConfig) => {
    const {
        publicRuntimeConfig: {
            apiRequestTimeoutMs, museApiHost, museWebHost
        },
        serverRuntimeConfig: { emailAlertToken }
    } = getConfig();

    const config = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                Authorization: `Shared-Token token=${emailAlertToken}`,
                Origin: museWebHost,
            }
        },
        ...axiosOptions
    };
    const path = `${museApiHost}/api/job_alerts/subscribe`;

    try {
        const response = await axios.post(path, values, config);
        return response.data;
    } catch (error) {
        const msg = `Error subscribing the user at ${path}, job ID: ${values.jobId}, status: ${error.response?.status}`;
        const err = JSON.stringify(error.response?.data?.error);
        console.error(msg, err);
        throw new ApiError("ApiError", error.response?.status, err);
    }
};

/**
 * newsletter optin from the public API.
 */
export const newsletterSubscribeRequest = async (values: NewsletterSubmissionValues, axiosOptions?: AxiosRequestConfig) => {
    const { publicRuntimeConfig: { apiRequestTimeoutMs, apiBase } } = getConfig();

    const config: AxiosRequestConfig = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                "Content-Type": "application/json",
            }
        },
        ...axiosOptions
    };
    const path = `${apiBase}/newsletter/subscribe`;

    try {
        const response = await axios.post(path, values, config);
        if (response.status === 200 || response.status === 201) {
            return true;
        }
        throw new Error(`Sendgrid newsletter subscription was unsuccessful. Status: ${response.status}`);
    } catch (error) {
        const msg = "Error: Sendgrid newsletter subscription was unsuccessful.";
        const err = JSON.stringify(formatLog({ error }));
        console.error(msg, err);
        return false;
    }
};

/**
 * newsletter opt in from the service API.
 */
export const newsletterApiSubscribeRequest = async (email: string) => {
    const {
        publicRuntimeConfig: {
            apiRequestTimeoutMs, museApiHost, museWebHost
        },
        serverRuntimeConfig: { emailAlertToken }
    } = getConfig();

    const emailSubscription = {
        email,
        page_section: "search"
    };

    const config = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                Authorization: `Shared-Token token=${emailAlertToken}`,
                Origin: museWebHost,
            }
        },
    };

    const path = `${museApiHost}/api/newsletter/subscribe_with_token`;
    try {
        const response = await axios.post(path, emailSubscription, config);
        return response?.data ?? {};
    } catch (error) {
        const msg = "Error: Unable to subscribe the user to Newsletters";
        const err = JSON.stringify(formatLog({ error }));
        console.error(msg, err);
        return null;
    }
};

export const combinedSubscribe = async (values?: Record<string, any>): Promise<any> => {
    const {
        alerts,
        email,
        newsletter,
        ...restProps
    } = values;
    // Remove alerts from this payload
    const jobAlertProps = { email, newsletter, ...restProps } as JobAlertsSubmissionValues;

    if (alerts !== "true" && newsletter === "true") {
        const result = newsletterSubscribeRequest({ email }).catch(console.error);
        return result;
    }

    const result = jobAlertsSubscribeRequest(jobAlertProps).catch(console.error);
    return result;
};

/**
 * Request user from the service API.
 */
export const fetchApiUser = async (oskarId = "", sessionId = "", axiosOptions?: AxiosRequestConfig) => {
    const {
        publicRuntimeConfig: {
            apiRequestTimeoutMs, museApiHost, museHost
        }
    } = getConfig();
    const config: AxiosRequestConfig = {
        ...{
            timeout: apiRequestTimeoutMs,
            headers: {
                "Content-Type": "application/json",
                cookie: `oskar_id=${oskarId}; session_id=${sessionId}`,
                referer: museHost
            }
        },
        ...axiosOptions
    };
    const path = `${museApiHost}/api/user`;
    try {
        const response = await axios(path, config);
        return response;
    } catch (error) {
        const msg = `Search Renderer was unable to request the user from ${path}.`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        return null;
    }
};

/**
 * Fetches job title cities from the API or cache.
 *
 * @param {string} title - The job title.
 * @param {string} state - The state.
 * @returns {Promise<JobTitleCities>} - The job title cities.
 * @throws {Error} - If there is an error fetching the data.
 */
export const fetchJobTitleCities = async (title: string, state: string): Promise<JobTitleCities | null> => {
    const {
        serverRuntimeConfig: { mockApiResponses },
        publicRuntimeConfig: { staticJSONAssetsHost },
    } = getConfig();
    const cacheTitle = `${title}-${state}`;
    const cachedItem = apiCache.get(cacheTitle);

    if (mockApiResponses) {
        return mockJobsCities as JobTitleCities;
    }

    if (cachedItem === undefined) {
        const prefix = `${staticJSONAssetsHost}/list-renderer/job_normalizer/titles/title`;
        const path = `${prefix}/${title}/${state}.json`;
        try {
            const response = await axios.get(path, { timeout: 3000 });
            const result = response.data;
            apiCache.set(cacheTitle, result);
            console.info(`Cache refreshed, ${cacheTitle} retrieved from API.`);
            return result;
        } catch (error) {
            const msg = getFetchError("JSON", "Back-End", path);
            console.error(msg, JSON.stringify(formatLog({ error })));
            return null;
        }
    }

    console.info(`Cache existing, ${cacheTitle} retrieved from cache.`);
    return cachedItem as JobTitleCities;
};
